<template>
  <div class="mx-auto max-w-[var(--container)] px-6 py-8">
    <Commerce-Breadcrumb :items="[
      { label: 'خانه', to: 'https://payjib.com' },
      { label: 'گیفت‌کارت' },
    ]" />

    <h1 class="mt-4 text-2xl font-extrabold md:text-3xl">
      خرید گیفت‌کارت
    </h1>

    <p class="mt-2 text-muted-foreground">
      برند و ریجن موردنظر را انتخاب کنید. کد دیجیتال پس از انجام سفارش از طریق ایمیل
      و پنل کاربری ارسال می‌شود.
    </p>

    <div v-if="loadingServices" class="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <CardSkeleton v-for="i in 8" :key="i" variant="service" />
    </div>

    <ul v-else-if="giftCardServices.length" class="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <li v-for="g in giftCardServices" :key="g.id">
        <NuxtLink :to="`/${g.slug}`">
          <Commerce-Card clickable class="flex flex-col gap-3 p-5">
            <div aria-hidden="true"
              class="grid h-12 w-12 place-items-center rounded-[var(--radius-md)] bg-secondary text-xl text-primary">
              ◫
            </div>

            <div class="font-bold">
              {{ g.name }}
            </div>

            <div class="text-xs text-muted-foreground">
              {{ g.regions.join('، ') }}
            </div>

            <div class="mt-auto text-sm font-semibold text-primary">
              انتخاب و خرید ›
            </div>
          </Commerce-Card>
        </NuxtLink>
      </li>
    </ul>

    <Pagination v-if="totalItems > 0" :currentPage="currentPage" :itemsPerPage="itemsPerPage" :totalItems="totalItems"
      appearance="commerce" @update:currentPage="changePage" />

    <div v-else class="mt-6">
      <Commerce-EmptyState glyph="⊘" title="گیفت‌کارتی پیدا نشد" body="در حال حاضر گیفت‌کارتی برای نمایش وجود ندارد." />
    </div>
  </div>
</template>

<script setup lang="ts">
const siteUrl = useSiteUrl()

import { computed, watch } from 'vue'
import { useSeoMeta } from '#app'
import type { Service } from '~/api/typings'

useSeoMeta({
  title: 'خرید گیفت‌کارت - پی‌جیب',
  description:
    'برند و ریجن موردنظر را انتخاب کنید. کد دیجیتال پس از انجام سفارش از طریق ایمیل و پنل کاربری ارسال می‌شود.',

  robots: 'index,follow',

  ogTitle: 'خرید گیفت‌کارت - پی‌جیب',
  ogDescription:
    'برند و ریجن موردنظر را انتخاب کنید. کد دیجیتال پس از انجام سفارش از طریق ایمیل و پنل کاربری ارسال می‌شود.',
  ogImage: siteUrl('/services/payjib.webp'),
  ogUrl: siteUrl('/services/gift-cards/'),
  ogType: 'website',
  ogSiteName: 'پی‌جیب',
  ogLocale: 'fa_IR',

  twitterCard: 'summary_large_image',
  twitterTitle: 'خرید گیفت‌کارت - پی‌جیب',
  twitterDescription:
    'برند و ریجن موردنظر را انتخاب کنید. کد دیجیتال پس از انجام سفارش از طریق ایمیل و پنل کاربری ارسال می‌شود.',
  twitterImage: siteUrl('/services/payjib.webp'),
})

const { categories, loadingCategories } = useCategories()

const { services, pagination, loadingServices, setFilters, setPage } = useServices({
  per_page: 16,
})

const currentPage = computed(() => pagination.value?.currentPage ?? 1)

const totalItems = computed(() => pagination.value?.total ?? 0)

const itemsPerPage = computed(() => pagination.value?.perPage ?? 15)

const changePage = (page: number) => {
  setPage(page)
}

const giftCardCategoryId = computed(() => categories.value?.find((category) => category.slug === 'gift-cards')?.id)

watch(
  giftCardCategoryId,
  (categoryId) => {
    if (!categoryId) return

    setFilters({
      category_id: categoryId,
    })
  },
  {
    immediate: true,
  }
)

const giftCardServices = computed(() =>
  services.value.map((service: Service) => ({
    ...service,
    regions: service.plans?.[0]?.prices?.map((price) => price.region?.name).filter(Boolean) ?? [],
  }))
)
</script>
